import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Filter, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { api } from "@/lib/api";
import useRedirectFallback from "@/lib/useRedirectFallback";
import ProductCard from "@/components/ProductCard";
import Seo, { categorySchema, breadcrumbSchema, itemListSchema } from "@/components/Seo";
import EditOnSiteLink from "@/components/EditOnSiteLink";

// Color palette for category hero fallbacks (no image set)
const CAT_GRADIENT = {
  halloween: "linear-gradient(135deg, #FF6B00 0%, #4D00FF 100%)",
  couples: "linear-gradient(135deg, #FF3366 0%, #FF6B00 100%)",
  kids: "linear-gradient(135deg, #FFD814 0%, #FF3366 100%)",
  group: "linear-gradient(135deg, #4D00FF 0%, #FF3366 100%)",
  "pop-culture": "linear-gradient(135deg, #FF3366 0%, #4D00FF 100%)",
  decades: "linear-gradient(135deg, #FFD814 0%, #FF6B00 100%)",
  "party-supplies": "linear-gradient(135deg, #FF6B00 0%, #FFD814 100%)",
  accessories: "linear-gradient(135deg, #4D00FF 0%, #FF6B00 100%)",
  pets: "linear-gradient(135deg, #FFD814 0%, #4D00FF 100%)",
};

function CategoryHeroImage({ cat }) {
  const [errored, setErrored] = useState(false);
  const showFallback = !cat.image_url || errored;
  if (showFallback) {
    return (
      <div
        className="brut bg-white aspect-[4/3] overflow-hidden relative grain flex items-center justify-center"
        style={{ background: CAT_GRADIENT[cat.slug] || "linear-gradient(135deg, #FF3366 0%, #4D00FF 100%)" }}
      >
        <div className="font-display text-white text-5xl md:text-7xl tracking-tighter leading-none text-center px-6 drop-shadow-[3px_3px_0_rgba(0,0,0,1)]">
          {cat.name}
        </div>
      </div>
    );
  }
  return (
    <div className="brut bg-white aspect-[4/3] overflow-hidden">
      <img
        src={cat.image_url}
        alt={cat.name}
        className="w-full h-full object-cover"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

export default function Category() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]); // empty = all
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // mobile drawer
  const [loading, setLoading] = useState(true);

  // When the category slug doesn't exist, consult the redirect map before
  // showing any 404 UI (legacy WordPress URLs like /product-category/foo land
  // on /category/foo only because of a redirect rule — but /category/bogus
  // that has no real category AND no redirect rule should still 404).
  useRedirectFallback(!loading && !cat);

  useEffect(() => {
    setLoading(true);
    api.get("/categories").then((r) => {
      const found = r.data.find((c) => c.slug === slug);
      setCat(found || null);
    });
    api.get("/products", { params: { category: slug, page_size: 100 } }).then((r) => {
      setItems(r.data.items);
      setLoading(false);
    });
  }, [slug]);

  // Client-side filtering for snappy UX (data is already paged at 100)
  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (vendors.length && !vendors.includes(p.vendor)) return false;
      if (featuredOnly && !p.featured) return false;
      const price = Number(p.price) || 0;
      if (minPrice !== "" && price < Number(minPrice)) return false;
      if (maxPrice !== "" && price > Number(maxPrice)) return false;
      return true;
    });
  }, [items, vendors, minPrice, maxPrice, featuredOnly]);

  // Derive vendor counts from full result set (not filtered) so toggling stays consistent
  const vendorCounts = useMemo(() => {
    const counts = { amazon: 0, ebay: 0 };
    for (const p of items) counts[p.vendor] = (counts[p.vendor] || 0) + 1;
    return counts;
  }, [items]);

  const toggleVendor = (v) => {
    setVendors((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const clearAll = () => {
    setVendors([]);
    setMinPrice("");
    setMaxPrice("");
    setFeaturedOnly(false);
  };

  const activeCount =
    vendors.length + (minPrice ? 1 : 0) + (maxPrice ? 1 : 0) + (featuredOnly ? 1 : 0);

  const seoSchema = useMemo(() => {
    if (!cat) return null;
    const itemList = itemListSchema(
      { name: cat.name, url: `/category/${slug}` },
      // Cap at first 30 products to keep the JSON-LD payload reasonable.
      filtered.slice(0, 30),
    );
    return {
      "@context": "https://schema.org",
      "@graph": [
        categorySchema(cat, filtered.length),
        breadcrumbSchema([
          { name: "Home", url: `${window.location.origin}/` },
          { name: cat.name, url: `${window.location.origin}/category/${slug}` },
        ]),
        ...(itemList ? [itemList] : []),
      ],
    };
  }, [cat, filtered, slug]);

  return (
    <div data-testid={`category-page-${slug}`}>
      {cat && (
        <Seo
          title={`${cat.name} Costumes & Party Supplies`}
          description={
            cat.description ||
            `Shop ${cat.name.toLowerCase()} costumes and party supplies from Amazon and eBay. Hand-picked, vetted, ready to ship.`
          }
          path={`/category/${slug}`}
          image={cat.image_url}
          type="website"
          schema={seoSchema}
        />
      )}
      {/* Header */}
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <div className="font-mono uppercase text-[11px] tracking-widest mb-3">
              <Link to="/" className="hover:pcs-text-pink">
                Home
              </Link>
              <span className="mx-2 opacity-50">/</span>
              <span>Category</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none">
              {cat?.name || slug}
            </h1>
            {cat && (
              <div className="mt-3">
                <EditOnSiteLink type="category" slug={cat.slug} />
              </div>
            )}
            {cat?.description && (
              <div
                className="mt-4 max-w-2xl text-base md:text-lg text-gray-800 space-y-3
                  [&_h1]:font-display [&_h1]:text-3xl [&_h1]:tracking-tight [&_h1]:mt-4 [&_h1]:mb-1
                  [&_h2]:font-display [&_h2]:text-2xl [&_h2]:tracking-tight [&_h2]:mt-4 [&_h2]:mb-1
                  [&_h3]:font-display [&_h3]:text-xl [&_h3]:tracking-tight [&_h3]:mt-3 [&_h3]:mb-1
                  [&_p]:my-2
                  [&_strong]:font-bold
                  [&_em]:italic
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ol]:space-y-1
                  [&_a]:underline [&_a]:pcs-text-pink hover:[&_a]:opacity-80
                  [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono"
                data-testid="category-description"
              >
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                  {cat.description}
                </ReactMarkdown>
              </div>
            )}
          </div>
          {cat && (
            <div className="md:col-span-5">
              <CategoryHeroImage cat={cat} />
            </div>
          )}
        </div>
      </section>

      {/* Results: sidebar + grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Mobile filter toggle */}
        <div className="lg:hidden mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowFilters(true)}
            className="brut bg-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press inline-flex items-center gap-2"
            data-testid="open-filters"
          >
            <Filter className="w-3.5 h-3.5" strokeWidth={2.5} />
            Filters {activeCount > 0 && <span className="pcs-bg-pink text-white px-1.5 rounded">{activeCount}</span>}
          </button>
          <span className="font-mono uppercase text-[11px] tracking-widest opacity-70">
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
          {/* Sidebar — desktop always; mobile drawer */}
          <aside
            className={`${
              showFilters ? "fixed inset-0 z-50 bg-black/40 lg:bg-transparent lg:static lg:inset-auto" : "hidden lg:block"
            }`}
            data-testid="filters-sidebar"
          >
            <div className="bg-cream pcs-bg-cream lg:bg-transparent absolute lg:relative right-0 top-0 h-full lg:h-auto w-[300px] lg:w-auto p-4 lg:p-0 overflow-y-auto border-l-2 lg:border-l-0 border-black shadow-2xl lg:shadow-none">
              <div className="flex items-center justify-between mb-4 lg:hidden">
                <div className="font-display text-2xl">Filters</div>
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="brut bg-white p-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <FiltersBlock
                vendors={vendors}
                toggleVendor={toggleVendor}
                vendorCounts={vendorCounts}
                minPrice={minPrice}
                setMinPrice={setMinPrice}
                maxPrice={maxPrice}
                setMaxPrice={setMaxPrice}
                featuredOnly={featuredOnly}
                setFeaturedOnly={setFeaturedOnly}
                activeCount={activeCount}
                clearAll={clearAll}
              />
            </div>
          </aside>

          {/* Grid */}
          <div>
            <div className="hidden lg:flex items-center justify-between mb-4">
              <div className="font-mono uppercase text-[11px] tracking-widest opacity-70">
                {filtered.length} {filtered.length === 1 ? "item" : "items"}
                {activeCount > 0 && <span className="ml-2">· {activeCount} filter{activeCount === 1 ? "" : "s"} active</span>}
              </div>
            </div>

            {loading ? (
              <div className="font-mono uppercase text-xs tracking-widest opacity-70">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="brut bg-white p-8 text-center" data-testid="empty-state">
                <div className="font-display text-3xl mb-2">
                  {items.length === 0 ? "Nothing here yet." : "No matches."}
                </div>
                <p className="text-gray-700 mb-4">
                  {items.length === 0
                    ? "We're still curating this category. Try another mood?"
                    : "Loosen the filters or clear them to see all items."}
                </p>
                {items.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="brut pcs-bg-pink text-white inline-block px-4 py-2 font-bold uppercase text-sm brut-press"
                  >
                    Clear filters
                  </button>
                ) : (
                  <Link
                    to="/"
                    className="brut pcs-bg-pink text-white inline-block px-4 py-2 font-bold uppercase text-sm brut-press"
                  >
                    Back home
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                {filtered.map((p) => (
                  <ProductCard key={p.slug} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FiltersBlock({
  vendors,
  toggleVendor,
  vendorCounts,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  featuredOnly,
  setFeaturedOnly,
  activeCount,
  clearAll,
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="font-display text-2xl tracking-tight">Filters</div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="clear-filters"
            className="font-mono uppercase text-[10px] tracking-widest brut bg-white px-2 py-1 brut-press"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="brut bg-white p-4">
        <div className="font-mono uppercase text-[10px] tracking-widest opacity-70 mb-2">
          Vendor
        </div>
        <div className="space-y-2">
          {[
            { v: "amazon", label: "Amazon" },
            { v: "ebay", label: "eBay" },
          ].map((opt) => (
            <label
              key={opt.v}
              className="flex items-center gap-2 cursor-pointer text-sm"
              data-testid={`filter-vendor-${opt.v}`}
            >
              <input
                type="checkbox"
                checked={vendors.includes(opt.v)}
                onChange={() => toggleVendor(opt.v)}
                className="w-4 h-4 border-2 border-black"
              />
              <span className="flex-1 font-bold">{opt.label}</span>
              <span className="font-mono text-[10px] opacity-60">
                {vendorCounts[opt.v] || 0}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="brut bg-white p-4">
        <div className="font-mono uppercase text-[10px] tracking-widest opacity-70 mb-2">
          Price range
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            min="0"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            placeholder="$ min"
            data-testid="filter-min-price"
            className="w-full brut bg-white px-2 py-1.5 text-sm focus:outline-none"
          />
          <span className="opacity-50">–</span>
          <input
            type="number"
            min="0"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            placeholder="$ max"
            data-testid="filter-max-price"
            className="w-full brut bg-white px-2 py-1.5 text-sm focus:outline-none"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {[
            ["Under $20", 0, 20],
            ["$20–50", 20, 50],
            ["$50–100", 50, 100],
            ["$100+", 100, ""],
          ].map(([label, lo, hi]) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setMinPrice(String(lo));
                setMaxPrice(hi === "" ? "" : String(hi));
              }}
              className="font-mono uppercase text-[9px] tracking-widest brut bg-white px-2 py-1 brut-press"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <label
        className="brut bg-white p-4 flex items-center gap-2 cursor-pointer"
        data-testid="filter-featured"
      >
        <input
          type="checkbox"
          checked={featuredOnly}
          onChange={(e) => setFeaturedOnly(e.target.checked)}
          className="w-4 h-4 border-2 border-black"
        />
        <span className="font-bold text-sm">★ Editor picks only</span>
      </label>
    </div>
  );
}
